/**
 * SOLID Review
 * S — Strong. One use case: EnterCodeScreen's Continue — no code hosts; a code must exist before the game screen opens.
 * O — Strong. What a code means is the Domain's decision.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The session's deps, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandTableCode, ICommandTableCodeChecked } from '../../domain/ports/schemas/SessionMoves';
import type { IPayloadServerRoomCode } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE } from '../shared/ClientSteps';
import type { IConfigDepSession } from './IConfigDepSession';

/**
 * EnterCodeScreen's Continue (FE2). Not a round trip: a plan with no code — the host intent — asks the server nothing, and a
 * failed lookup is answered in its own words.
 */
export class CheckTableCodeUseCase implements IUseCase<ICommandTableCode, TPortOutcome> {
  constructor(private readonly deps: IConfigDepSession) {}

  async execute(ctx: IContextRequest, command: ICommandTableCode): Promise<TPortOutcome> {
    const { decisions, step, rooms } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planTableCode(ctx, input, command));
    if (!plan.success) return plan;
    const code = plan.value.code;
    if (code === null) return DONE;
    const roomCode: IPayloadServerRoomCode = { roomCode: code };
    const found = await rooms.getRoomByCode(ctx, roomCode);
    if (!found.success) return found;
    const checked: ICommandTableCodeChecked = { code, found: found.value !== null };
    return step.outcome(await step.run(ctx, (input) => decisions.tableCodeChecked(ctx, input, checked)));
  }
}
